import { normalizeOfficePathname, resolveRumViewName } from '@/features/observability/page/view-name';

type CorePage = 'workspace' | 'project';
type ReadySample = {
    name: 'workspace_ready' | 'project_ready';
    startTime: number;
    duration: number;
    context: {
        page: string;
        core_page: CorePage;
        measurement: 'initial_load';
        metric_version: 2;
        surface?: string;
    };
};

interface ReadyReporter {
    enabled: boolean;
    generation: number | null;
    isGenerationCurrent: (generation: number) => boolean;
    report: (
        name: string,
        sample: Omit<ReadySample, 'name'>,
        isValid: () => boolean
    ) => Promise<void>;
}

/** 首次文档只留一个成功时间；不跟踪失败率、重试或后续 SPA 打开。 */
export class CorePageReady {
    private target: { key: string; page: string; kind: CorePage } | undefined;
    private pending: ReadySample | undefined;
    private generation: number | null = null;
    private reporter: ReadyReporter | undefined;
    private discarded = false;
    private finished = false;
    private sending = false;

    constructor(
        private readonly timeOrigin: number,
        private readonly now: () => number,
        private dispose?: () => void
    ) {}

    visit(pathname: string, page: string): void {
        if (this.discarded || this.finished) return;
        const path = normalizeOfficePathname(pathname);
        const key = `${path}:${page}`;
        if (this.target) {
            if (this.target.key !== key) this.cancel();
            return;
        }
        if (path === '/workspace') return;
        const kind =
            /^\/workspace\/[^/]+$/.test(path) &&
            /^\/workspace\/\?\/(newchat|projects|connectors|experts|design-kit|infra)$/.test(page)
                ? 'workspace'
                : /^\/project\/[^/]+$/.test(path) && page === '/project/?'
                  ? 'project'
                  : undefined;
        if (!kind) {
            this.cancel();
            return;
        }
        this.target = { key, page, kind };
    }

    configure(reporter: ReadyReporter, denied: boolean): void {
        if (this.discarded || this.finished) return;
        if (denied || (this.generation !== null && reporter.generation !== this.generation)) {
            this.cancel();
            return;
        }
        this.generation ??= reporter.generation;
        this.reporter = reporter;
        this.flush();
    }

    ready(page: string): void {
        if (!this.isWaiting(page) || !this.target) return;
        this.pending = {
            name: this.target.kind === 'workspace' ? 'workspace_ready' : 'project_ready',
            startTime: this.timeOrigin,
            duration: Math.max(0, this.now()),
            context: {
                page,
                core_page: this.target.kind,
                measurement: 'initial_load',
                metric_version: 2,
                ...(this.target.kind === 'workspace' ? { surface: page.split('/').pop() } : {}),
            },
        };
        this.flush();
    }

    isWaiting(page: string): boolean {
        return (
            !this.discarded &&
            !this.finished &&
            !this.sending &&
            !this.pending &&
            this.target?.page === page
        );
    }

    cancel(): void {
        if (this.discarded || this.finished) return;
        this.discarded = true;
        this.pending = undefined;
        this.cleanup();
    }

    private cleanup(): void {
        const dispose = this.dispose;
        this.dispose = undefined;
        dispose?.();
    }

    private flush(): void {
        const reporter = this.reporter;
        if (!this.pending || !reporter?.enabled || this.generation === null) return;
        if (!reporter.isGenerationCurrent(this.generation)) {
            this.cancel();
            return;
        }
        const { name, ...sample } = this.pending;
        this.pending = undefined;
        this.sending = true;
        const finish = () => {
            this.finished = true;
            this.sending = false;
            this.cleanup();
        };
        try {
            void reporter
                .report(name, sample, () => !this.discarded)
                .catch(() => undefined)
                .finally(finish);
        } catch {
            // 遥测失败不影响页面就绪和原业务流程。
            finish();
        }
    }
}

let initialReady: CorePageReady | undefined;

/** 入口仅记录本地可见性，不加载 SDK、不发送事件。 */
export function initializeCorePageReady(): void {
    if (initialReady || typeof window === 'undefined') return;
    const onHidden = () => {
        if (document.visibilityState !== 'visible') initialReady?.cancel();
    };
    const onPageHide = () => initialReady?.cancel();
    initialReady = new CorePageReady(
        performance.timeOrigin,
        () => performance.now(),
        () => {
            document.removeEventListener('visibilitychange', onHidden);
            window.removeEventListener('pagehide', onPageHide);
        }
    );
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    const navigation = performance.getEntriesByType('navigation')[0];
    const initialUrl = new URL(navigation?.name || window.location.href, window.location.href);
    initialReady.visit(
        initialUrl.pathname,
        resolveRumViewName(initialUrl.pathname, initialUrl.search)
    );
    let previouslyHidden = false;
    try {
        previouslyHidden = performance
            .getEntriesByType('visibility-state')
            .some(entry => entry.name === 'hidden');
    } catch {
        // 不支持可见性历史的浏览器继续使用入口状态与后续事件。
    }
    if (document.visibilityState !== 'visible' || previouslyHidden) initialReady.cancel();
}

export function getCorePageReady(): CorePageReady | undefined {
    return initialReady;
}
